import React, { useEffect, useRef, useState } from 'react';
import { withTranslation } from '../../../component-decorators/with-translation';
import {
  Box,
  CustomModalLayout,
  Dropdown,
  SectionHelper,
  FormField,
  Heading,
  InputArea,
  Loader,
  Modal,
  Text,
} from '@wix/design-system';
import { Captcha } from 'wix-ui-core/captcha';
import type { WithTranslation as WithTranslationProps } from 'react-i18next';
import { EditorType, Origin } from '../../manage-installed-apps-page';
import type { ManagedApp } from '@wix/ambassador-devcenter-managedapps-v1-managed-app/types';
import { useHttpClient } from '../../../hooks/use-http-client';
import {
  appMarketSupportMailModalClosedX,
  appMarketSupportMailModalOpened,
} from '@wix/bi-logger-market/v2';
import { useBiLogger } from '../../../hooks/use-bi-logger';

export enum ManageAppsSupportModalTopic {
  BILLING_AND_UPGRADES = 'Billing and Upgrades',
  TECHNICAL_SUPPORT = 'Technical Support',
  FEATURE_REQUEST = 'Feature Request',
  OTHER = 'Other',
}

const enum SupportModalErrorTypes {
  GENERAL_ERROR = 'GENERAL_ERROR',
}

interface ISupportModalProps extends Partial<WithTranslationProps> {
  app: ManagedApp;
  msId: string;
  origin: Origin;
  baseUrl: string;
  authorizationHeader: string;

  showUserActionNotification(options: any): void;

  closeModal(): void;

  clickSource?: string;
  pageName?: string;
  editorType?: string;
}

// remove the wrapper once new translation hook is available
@withTranslation
export class SupportModal extends React.Component<ISupportModalProps> {
  render() {
    return <RefactoredSupportModal {...this.props} />;
  }
}

function RefactoredSupportModal({
  t,
  app,
  msId,
  origin,
  baseUrl,
  authorizationHeader,
  showUserActionNotification,
  closeModal,
  clickSource,
  pageName,
  editorType,
}: ISupportModalProps) {
  const biLogger = useBiLogger();
  const [description, setDescription] = useState('');
  const [topic, setTopic] = useState<ManageAppsSupportModalTopic>(undefined);
  const [showError, setShowError] = useState<SupportModalErrorTypes>(undefined);
  const [hasCounter, setHasCounter] = useState(false);
  const [descriptionStatus, setDescriptionStatus] = useState(undefined);
  const [topicStatus, setTopicStatus] = useState(undefined);
  const [captchaRefStatus, setCaptchaRefStatus] = useState<any>(undefined);
  const [verifiedToken, setVerifiedToken] = useState('');

  const captchaRef = useRef(null);
  const httpClient = useHttpClient();

  useEffect(() => {
    biLogger
      .report(
        appMarketSupportMailModalOpened({
          app_id: app?.appId,
          instance_id: app?.installationDetails?.instanceId,
          msid: msId,
          market:
            origin === Origin.BIZMGR
              ? editorType === EditorType.ADI
                ? 'adi_dashboard'
                : 'dashboard'
              : origin,
          is_app_installed: !!app?.installationDetails?.instanceId,
          origin: clickSource || 'context_menu',
          tag_type: pageName || 'manage_apps',
        }),
      )
      .catch((e) => console.log(e));
  }, []);

  const handleDescriptionChange = (e) => {
    const val = e.target.value;
    setDescription(val);
  };

  const handleTopicChange = ({ id }) => {
    setTopic(id);
  };

  const getSendEmailRequestObject = () => {
    return {
      description,
      topic,
      appInstanceId: app?.installationDetails?.instanceId,
      appID: app?.appId,
      msid: msId,
    };
  };

  const getVerifiedToken = () => {
    if (captchaRef.current) {
      return captchaRef.current.getVerificationToken();
    }
    return '';
  };

  const isCaptchaVerified = () => {
    if (captchaRef.current) {
      return captchaRef.current.isVerified();
    }
    return false;
  };

  const validateInputs = () => {
    let result = true;
    setDescriptionStatus(undefined);
    setTopicStatus(undefined);
    setCaptchaRefStatus(undefined);
    setShowError(undefined);

    if (description === '' || description.length < 20) {
      setDescriptionStatus('error');
      result = false;
    }
    if (!topic) {
      setTopicStatus('error');
      result = false;
    }
    if (!isCaptchaVerified()) {
      setCaptchaRefStatus(false);
      setShowError(SupportModalErrorTypes.GENERAL_ERROR);
      result = false;
    }
    return result;
  };

  const sendEmail = () => {
    const validInputs = validateInputs();
    if (validInputs) {
      httpClient
        .post(
          `${baseUrl}/_serverless/app-support/send-mail`,
          getSendEmailRequestObject(),
          {
            headers: {
              'content-type': 'application/json',
              authorization: authorizationHeader,
              withCredentials: true,
            },
          },
        )
        .then(() => {
          showUserActionNotification &&
            showUserActionNotification({
              message: t('manage-apps.support-modal.success'),
              biName: 'support-modal',
              type: 'SUCCESS',
            });
          closeModal();
        })
        .catch((e) => {
          console.error(e);
          showUserActionNotification &&
            showUserActionNotification({
              message: t('manage-apps.support-modal.fail'),
              biName: 'support-modal',
              type: 'ERROR',
            });
          closeModal();
        });
    }
  };

  const onCancelModal = () => {
    const appId = app?.appId;
    const appInstanceId = app?.installationDetails?.instanceId;
    biLogger
      .report(
        appMarketSupportMailModalClosedX({
          app_id: appId,
          instance_id: appInstanceId,
          msid: msId,
          market:
            origin === Origin.BIZMGR
              ? editorType === EditorType.ADI
                ? 'adi_dashboard'
                : 'dashboard'
              : origin,
          is_app_installed: !!appInstanceId,
          origin: clickSource || 'context_menu',
          tag_type: pageName || 'manage_apps',
        }),
      )
      .catch((e) => console.log(e));
    closeModal();
  };

  const appIcon = app?.appInfo?.icon;
  const appNameFromData = app?.appInfo?.name;
  const appName = appNameFromData?.replace('&amp;', '&');

  return (
    <CustomModalLayout
      width="600px"
      height="580px"
      title={<SupportModalTitle appIcon={appIcon} appName={appName} t={t} />}
      primaryButtonText={t('manage-apps.support-modal.send')}
      primaryButtonOnClick={sendEmail}
      secondaryButtonText={t('manage-apps.support-modal.cancel')}
      secondaryButtonOnClick={onCancelModal}
      onCloseButtonClick={onCancelModal}
    >
      {showError ? (
        <Box marginBottom="12px" direction="vertical">
          <SectionHelper appearance="danger" showCloseButton={false}>
            {t('manage-apps.support-modal.general-error')}
          </SectionHelper>
        </Box>
      ) : (
        <Box direction="vertical">
          <Box marginBottom="30px" direction="vertical">
            <FormField
              required
              label={t('manage-apps.support-modal.form.field.topic')}
            >
              <Dropdown
                dataHook="support-modal-topic-dropdown"
                onSelect={handleTopicChange}
                placeholder={t(
                  'manage-apps.support-modal.form.field.topic.placeholder',
                )}
                options={[
                  {
                    id: ManageAppsSupportModalTopic.BILLING_AND_UPGRADES,
                    value: t(
                      'manage-apps.support-modal.form.field.topic.option.billing-and-upgrades',
                    ),
                  },
                  {
                    id: ManageAppsSupportModalTopic.TECHNICAL_SUPPORT,
                    value: t(
                      'manage-apps.support-modal.form.field.topic.option.technical-support',
                    ),
                  },
                  {
                    id: ManageAppsSupportModalTopic.FEATURE_REQUEST,
                    value: t(
                      'manage-apps.support-modal.form.field.topic.option.feature-request',
                    ),
                  },
                  {
                    id: ManageAppsSupportModalTopic.OTHER,
                    value: t(
                      'manage-apps.support-modal.form.field.topic.option.other',
                    ),
                  },
                ]}
                statusMessage={t('manage-apps.support-modal.topic-error')}
                status={topicStatus}
                onFocus={() => setTopicStatus(undefined)}
              />
            </FormField>
          </Box>
          <Box marginBottom="24px" direction="vertical">
            <FormField
              required
              label={t('manage-apps.support-modal.form.field.description')}
            >
              <InputArea
                dataHook="description-input"
                hasCounter={hasCounter}
                maxLength={2048}
                status={descriptionStatus}
                onChange={handleDescriptionChange}
                minHeight="160px"
                onFocus={() => {
                  setHasCounter(true);
                  setDescriptionStatus(undefined);
                }}
                onBlur={() => setHasCounter(false)}
                placeholder={t(
                  'manage-apps.support-modal.form.field.description.placeholder',
                )}
                value={description}
                statusMessage={t('manage-apps.support-modal.description-error')}
                tooltipPlacement="left"
              />
            </FormField>
          </Box>
          <Box>
            <Captcha
              sitekey="6LejwKAUAAAAACs5B-wV9ndjxrE49x3lGztPznl_"
              loader={<Loader size="tiny" />}
              ref={(e) => (captchaRef.current = e)}
              onVerify={() => setVerifiedToken(getVerifiedToken())}
            />
          </Box>
        </Box>
      )}
    </CustomModalLayout>
  );
}

const AppIcon = ({ src }: { src: string }) => (
  <img
    data-hook="support-modal-app-icon"
    src={src}
    alt="app-icon"
    style={{
      width: '60px',
      height: '60px',
      marginRight: '18px',
      borderRadius: '6px',
      border: '1px solid rgb(240, 244, 247)',
      boxSizing: 'border-box',
    }}
  />
);

function SupportModalTitle(props: {
  appIcon: string;
  appName: string;
  t: any;
}) {
  return (
    <Box verticalAlign="top">
      <AppIcon src={props.appIcon} />
      <Box direction="vertical" verticalAlign="top">
        <Heading appearance="H2" dataHook="support-modal-title">
          {props.t('manage-apps.support-modal.title')}
        </Heading>
        <Text secondary dataHook="support-modal-subtitle">
          {props
            .t('manage-apps.support-modal.subtitle', { appName: props.appName })
            .replace('&amp;', '&')}
        </Text>
      </Box>
    </Box>
  );
}
